import { computed, ref } from 'vue';

import axios from '@/plugins/axios.js';

const optionsCache = ref(null);
const optionsMetaCache = ref(null);
const presetsCache = ref(null);
const isLoading = ref(false);
const loadingError = ref(null);
const isLoadingPresets = ref(false);
const presetsError = ref(null);

const lookupFromCategories = categories => {
    const lookup = {};

    (categories || []).forEach(category => {
        (category.subcategories || []).forEach(subcategory => {
            (subcategory.options || []).forEach(option => {
                lookup[option.key] = option.label;
            });
        });
    });

    return lookup;
};

const extractCollection = (response, fallback = []) => {
    const payload = response?.data;

    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;

    return fallback;
};

export const normalizeVoiceCastSelectionKeys = (selected, options, legacyVoiceKeys = {}) => {
    if (!Array.isArray(selected)) {
        return [];
    }

    const optionList = Array.isArray(options) ? options : [];
    const optionKeys = new Set(
        optionList.map(option => option?.key).filter(key => typeof key === 'string' && key !== '')
    );
    const voiceIdToKey = {};

    optionList.forEach(option => {
        const voiceId = option?.meta?.voice_id;
        const key = option?.key;

        if (typeof voiceId === 'string' && voiceId !== '' && typeof key === 'string' && key !== '') {
            voiceIdToKey[voiceId] = key;
        }
    });

    const normalized = [];

    selected.forEach(rawKey => {
        if (typeof rawKey !== 'string') {
            return;
        }

        const key = rawKey.trim();
        if (key === '') {
            return;
        }

        let resolvedKey = null;

        if (optionKeys.has(key)) {
            resolvedKey = key;
        } else if (typeof legacyVoiceKeys[key] === 'string') {
            const legacyVoiceId = legacyVoiceKeys[key].trim();
            resolvedKey = voiceIdToKey[legacyVoiceId] || (optionKeys.has(legacyVoiceId) ? legacyVoiceId : null);
        }

        if (resolvedKey && !normalized.includes(resolvedKey)) {
            normalized.push(resolvedKey);
        }
    });

    return normalized;
};

export const buildCommaSeparatedTextFromSelections = (selections, categories) => {
    if (!selections || typeof selections !== 'object') {
        return '';
    }

    const lookup = lookupFromCategories(categories);
    const deliveryCategoryKeys = new Set(['delivery_pacing', 'tone_emotional_style', 'pacing_delivery']);
    const deliverySubcategoryKeys = new Set([
        'tone',
        'pacing',
        'interaction_style',
        'relationship_archetypes',
        'creator_mode',
    ]);
    const ignoredCategoryKeys = new Set(['voice_cast']);
    const ignoredSubcategoryKeys = new Set(['voices']);
    const deliveryLabels = [];
    const structureLabels = [];

    const humanize = value =>
        value
            .replaceAll('_', ' ')
            .replace(/\s+/g, ' ')
            .trim()
            .replace(/^\w/, char => char.toUpperCase());

    (categories || []).forEach(category => {
        if (ignoredCategoryKeys.has(category.key)) {
            return;
        }

        const categorySelection = selections?.[category.key];
        if (!categorySelection || typeof categorySelection !== 'object') {
            return;
        }

        (category.subcategories || []).forEach(subcategory => {
            if (ignoredSubcategoryKeys.has(subcategory.key)) {
                return;
            }

            const selected = categorySelection?.[subcategory.key];
            if (selected === undefined || selected === null || selected === '') {
                return;
            }

            const keys = Array.isArray(selected) ? selected : [selected];
            keys.forEach(key => {
                if (typeof key !== 'string') return;
                const isDelivery =
                    deliveryCategoryKeys.has(category.key) || deliverySubcategoryKeys.has(subcategory.key);
                const label = lookup[key] || humanize(key);
                if (isDelivery) {
                    if (!deliveryLabels.includes(label)) {
                        deliveryLabels.push(label);
                    }
                    return;
                }

                if (!structureLabels.includes(label)) {
                    structureLabels.push(label);
                }
            });
        });
    });

    if (deliveryLabels.length === 0 && structureLabels.length === 0) {
        return '';
    }

    const humanList = values => {
        if (values.length === 1) return values[0];
        if (values.length === 2) return `${values[0]} and ${values[1]}`;
        return `${values.slice(0, -1).join(', ')}, and ${values[values.length - 1]}`;
    };

    const parts = [];
    if (deliveryLabels.length) {
        parts.push(`Performance direction: ${humanList(deliveryLabels)}`);
    }
    if (structureLabels.length) {
        parts.push(`Episode plan: ${humanList(structureLabels)}`);
    }

    return `${parts.join('. ')}. Keep transitions natural and maintain a consistent two-host flow.`;
};

export function usePodcastConfiguration() {
    const categories = computed(() => optionsCache.value || []);
    const presets = computed(() => presetsCache.value || []);

    const load = async ({ force = false } = {}) => {
        if (!force && Array.isArray(optionsCache.value) && optionsCache.value.length > 0) {
            return;
        }

        isLoading.value = true;
        loadingError.value = null;

        try {
            const optionsResponse = await axios.get('/api/podcast-configuration/options');
            const nextOptions = extractCollection(optionsResponse, []);
            const nextMeta =
                optionsResponse?.data?.meta && typeof optionsResponse.data.meta === 'object'
                    ? optionsResponse.data.meta
                    : {};

            if (Array.isArray(nextOptions) && nextOptions.length > 0) {
                optionsCache.value = nextOptions;
                optionsMetaCache.value = nextMeta;
                return;
            }

            optionsCache.value = [];
            optionsMetaCache.value = nextMeta;
            loadingError.value = 'Unable to load podcast configuration options.';
        } catch (error) {
            loadingError.value = error?.response?.data?.message || 'Unable to load podcast configuration options.';
        } finally {
            isLoading.value = false;
        }
    };

    const loadPresets = async ({ force = false } = {}) => {
        if (!force && Array.isArray(presetsCache.value)) return presetsCache.value;

        isLoadingPresets.value = true;
        presetsError.value = null;

        try {
            const response = await axios.get('/api/podcast-configuration/presets');
            const nextPresets = extractCollection(response, []).map(preset => ({
                id: preset.id,
                name: preset.name,
                selections: preset.selections || {},
            }));

            presetsCache.value = nextPresets;

            return nextPresets;
        } catch (error) {
            presetsError.value = error?.response?.data?.message || 'Unable to load podcast presets.';
            presetsCache.value = [];
            return [];
        } finally {
            isLoadingPresets.value = false;
        }
    };

    const savePreset = async ({ name, selections, overwrite = true }) => {
        const response = await axios.post('/api/podcast-configuration/presets', {
            name,
            selections,
            overwrite,
        });

        await loadPresets({ force: true });

        return response?.data?.data || null;
    };

    const deletePreset = async presetId => {
        await axios.delete(`/api/podcast-configuration/presets/${presetId}`);
        await loadPresets({ force: true });
    };

    const clearPresetCache = () => {
        presetsCache.value = null;
    };

    const buildCommaSeparatedText = selections => buildCommaSeparatedTextFromSelections(selections, categories.value);

    const clearOptionsCache = () => {
        optionsCache.value = null;
        optionsMetaCache.value = null;
    };

    return {
        categories,
        optionsMeta: computed(() => optionsMetaCache.value || {}),
        loading: isLoading,
        error: loadingError,
        presets,
        presetsLoading: isLoadingPresets,
        presetsError,
        load,
        clearOptionsCache,
        loadPresets,
        savePreset,
        deletePreset,
        clearPresetCache,
        buildCommaSeparatedText,
    };
}
